import React, { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import toast from 'react-hot-toast'

import { useCartRedux, useOrderRedux } from '@hooks'
import { ROUTES } from '@common'
import { Loader, PaymentInstructions } from '@components'
import { CREATE_PAYMENT_INTENT, CHECK_ORDER_PAYMENT_STATUS } from '@services'

export default function OrderCompletePage() {
    const { orderId } = useParams()
    const { state } = useLocation()
    const navigate = useNavigate()
    const { completeOrder, setCompleteOrder, setSearchedOrder, fetchOrderById } = useOrderRedux()
    const { clearCart } = useCartRedux()

    const [createPaymentIntent, { loading: isGeneratingQr }] = useMutation(CREATE_PAYMENT_INTENT, {
        onCompleted: data => {
            if (data?.createPaymentIntent?.result) {
                const { id: paymentIntentId, client_secret: clientSecret } = data.createPaymentIntent.paymentIntent
                navigate(
                    ROUTES.Payment.root
                        .replace(':orderId', completeOrder.orderId)
                        .replace(':paymentIntentId', paymentIntentId)
                        .replace(':clientSecret', clientSecret)
                )
            } else {
                toast.error(data?.createPaymentIntent?.error || 'Failed to generate PayNow QR')
            }
        },
        onError: err => {
            console.error('Payment intent error:', err)
            toast.error(err?.message || 'Failed to generate PayNow QR')
        },
    })

    const [checkOrderStatus, { loading: isCheckingStatus }] = useLazyQuery(CHECK_ORDER_PAYMENT_STATUS, {
        onCompleted: data => {
            if (data?.checkOrderPaymentStatus?.result) {
                const order = data.checkOrderPaymentStatus.order
                if (order.payment?.paidAt) {
                    setCompleteOrder({ ...completeOrder, ...order })
                    setSearchedOrder({ ...completeOrder, ...order })
                }

                navigate(ROUTES.Order.number.replace(':orderNumber', order.number))
            } else {
                toast.error(data?.checkOrderPaymentStatus?.error || 'Failed to check payment status')
            }
        },
        onError: err => {
            console.error('Check payment status error:', err)
            toast.error(err?.message || 'Failed to check payment status')
        },
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        clearCart()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (completeOrder === null) {
            const fetchOrderByIdAsync = async () => {
                const res = await fetchOrderById(orderId)
                if (!res.payload) {
                    navigate('/not-found', { replace: true })
                }
            }

            fetchOrderByIdAsync()
        }
    }, [completeOrder, orderId, fetchOrderById, navigate])

    const onGeneratePaynowQr = async () => {
        await createPaymentIntent({
            variables: {
                input: {
                    orderId: completeOrder.orderId,
                    orderNumber: completeOrder.number,
                    amount: completeOrder.summary?.finalAmount || 0,
                },
            },
        })
    }

    const onCheckOrderStatus = () => {
        checkOrderStatus({
            variables: {
                orderId: completeOrder.orderId,
            },
        })
    }

    if (!completeOrder || isGeneratingQr || isCheckingStatus) {
        return <Loader />
    }

    return (
        <div className="order-complete-page">
            <h5 className="mb-4">Thank you for placing the order</h5>
            <div className="mb-3 d-flex flex-column align-items-center">
                <h5>Order number</h5>
                <h1 className="text-primary m-0" style={{ fontSize: '3.5rem' }}>
                    {completeOrder.number}
                </h1>
            </div>

            {/* {completeOrder.table?.id && (
                <div className="mb-3 d-flex flex-column align-items-center">
                    <p>Table Number</p>
                    <h2 className="text-primary m-0">{completeOrder.table.name}</h2>
                </div>
            )} */}

            <PaymentInstructions
                paymentType={state?.isPaid ? 'PAYNOW' : completeOrder.payment?.type}
                onGeneratePaynowQr={onGeneratePaynowQr}
                isGeneratingQr={isGeneratingQr}
            />

            <button
                className="btn btn-primary mt-3 d-flex align-items-center justify-content-center"
                style={{ width: '100%' }}
                onClick={onCheckOrderStatus}
            >
                Check Order Status
                <i className="bx bx-right-arrow-alt bx-sm ms-2"></i>
            </button>
        </div>
    )
}
